import { apiService } from './api'

interface BlogCategory {
  id: string
  name: string
  slug: string
  description?: string
  color?: string
  created_at: string
  updated_at: string
}

interface BlogTag {
  id: string
  name: string
  slug: string
  description?: string
  created_at: string
  updated_at: string
}

interface CacheEntry<T> {
  data: T
  timestamp: number
  expiresAt: number
}

interface InFlightRequest<T> {
  promise: Promise<T>
  abortController: AbortController
}

class BlogMetadataCache {
  private categoriesCache: CacheEntry<BlogCategory[]> | null = null
  private tagsCache: CacheEntry<BlogTag[]> | null = null
  private categoriesInFlight: InFlightRequest<BlogCategory[]> | null = null
  private tagsInFlight: InFlightRequest<BlogTag[]> | null = null
  private readonly TTL = 5 * 60 * 1000

  private isExpired(entry: CacheEntry<unknown> | null): boolean {
    if (!entry) return true
    return Date.now() > entry.expiresAt
  }

  private async fetchWithDeduplication<T>(
    key: 'categories' | 'tags',
    fetcher: () => Promise<T>
  ): Promise<T> {
    const inFlightKey = key === 'categories' ? 'categoriesInFlight' : 'tagsInFlight'
    const inFlight = this[inFlightKey] as InFlightRequest<T> | null

    if (inFlight) {
      console.log(`[BlogMetadataCache] Returning in-flight request for ${key}`)
      return inFlight.promise
    }

    const abortController = new AbortController()
    const promise = fetcher()
      .then(data => {
        this[inFlightKey] = null
        return data
      })
      .catch(error => {
        this[inFlightKey] = null
        throw error
      })

    this[inFlightKey] = { promise, abortController } as any
    return promise
  }

  async getCategories(forceRefresh = false): Promise<BlogCategory[]> {
    if (!forceRefresh && !this.isExpired(this.categoriesCache)) {
      console.log('[BlogMetadataCache] Returning cached categories')
      return this.categoriesCache!.data
    }

    console.log('[BlogMetadataCache] Fetching fresh categories')
    const data = await this.fetchWithDeduplication<BlogCategory[]>(
      'categories',
      () => apiService.blog.categories.list()
    )

    this.categoriesCache = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.TTL,
    }

    return data
  }

  async getTags(forceRefresh = false): Promise<BlogTag[]> {
    if (!forceRefresh && !this.isExpired(this.tagsCache)) {
      console.log('[BlogMetadataCache] Returning cached tags')
      return this.tagsCache!.data
    }

    console.log('[BlogMetadataCache] Fetching fresh tags')
    const data = await this.fetchWithDeduplication<BlogTag[]>(
      'tags',
      () => apiService.blog.tags.list()
    )

    this.tagsCache = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.TTL,
    }

    return data
  }

  async getBoth(forceRefresh = false): Promise<{
    categories: BlogCategory[]
    tags: BlogTag[]
  }> {
    const [categories, tags] = await Promise.all([
      this.getCategories(forceRefresh),
      this.getTags(forceRefresh),
    ])

    return { categories, tags }
  }

  invalidateCategories(): void {
    console.log('[BlogMetadataCache] Invalidating categories cache')
    this.categoriesCache = null
    if (this.categoriesInFlight) {
      this.categoriesInFlight.abortController.abort()
      this.categoriesInFlight = null
    }
  }

  invalidateTags(): void {
    console.log('[BlogMetadataCache] Invalidating tags cache')
    this.tagsCache = null
    if (this.tagsInFlight) {
      this.tagsInFlight.abortController.abort()
      this.tagsInFlight = null
    }
  }

  invalidateAll(): void {
    console.log('[BlogMetadataCache] Invalidating all caches')
    this.invalidateCategories()
    this.invalidateTags()
  }

  getCacheStatus() {
    return {
      categories: {
        cached: !this.isExpired(this.categoriesCache),
        age: this.categoriesCache
          ? Date.now() - this.categoriesCache.timestamp
          : null,
        inFlight: !!this.categoriesInFlight,
      },
      tags: {
        cached: !this.isExpired(this.tagsCache),
        age: this.tagsCache ? Date.now() - this.tagsCache.timestamp : null,
        inFlight: !!this.tagsInFlight,
      },
    }
  }
}

export const blogMetadataCache = new BlogMetadataCache()
export type { BlogCategory, BlogTag }